import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useLayoutEffect
} from 'react';
import { useDrag } from '@use-gesture/react';
import gsap from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';

import { Stepper, Step } from '../Stepper';
import { STEPPER_THEME, type StepperTheme } from '../Stepper.theme';

gsap.registerPlugin(ScrollToPlugin);

export interface QuestionsAnswersHandle {
  container: HTMLDivElement | null;
  content: HTMLDivElement | null;
  title: HTMLHeadingElement | null;
  paragraph: HTMLParagraphElement | null;
  questionBubble: HTMLParagraphElement | null;
  answerBubble: HTMLParagraphElement | null;
}

export interface QuestionsAnswersProps {
  isDarkMode: boolean;
  startAnimation: boolean;
}

interface Showcase {
  id: number;
  category: string;
  question: string;
  answer: string;
}

const SHOWCASES: Showcase[] = [
  {
    id: 0,
    category: 'Definitions',
    question: "What is an ‘Oculist’?",
    answer:
      'An oculist is an old-fashioned word for an eye doctor, a profession that today is replaced by optometrists and ophthalmologists.'
  },
  {
    id: 1,
    category: 'Search the plot',
    question: 'Where does Gatsby first meet Daisy after the war?',
    answer:
      'Gatsby reunites with Daisy at Nick Carraway’s cottage in West Egg, carefully arranging the meeting through Jordan Baker.'
  },
  {
    id: 2,
    category: 'Context & author',
    question:
      'How does Fitzgerald’s own life influence the portrayal of wealth in The Great Gatsby?',
    answer:
      'Fitzgerald’s experiences with sudden wealth, and disillusionment deeply inform the novel’s critique of excess status.'
  },
  {
    id: 3,
    category: 'Literary & philosophical',
    question: 'Is Gatsby a tragic hero in the classical sense?',
    answer:
      'Gatsby mirrors the tragic hero through his idealism and fatal flaw: an unwavering belief that the past can be recreated through sheer will.'
  }
];

const QuestionsAnswers = forwardRef<
  QuestionsAnswersHandle,
  QuestionsAnswersProps
>(({ isDarkMode }, ref) => {
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const paragraphRef = useRef<HTMLParagraphElement>(null);
  const questionRef = useRef<HTMLParagraphElement>(null);
  const answerTextRef = useRef<HTMLParagraphElement>(null);

  const mobileCategoriesContainerRef = useRef<HTMLDivElement>(null);
  // Step renders a div, so store div refs (not buttons)
  const mobileCategoryRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [activeIndex, setActiveIndex] = useState(0);

  // Keep a ref in sync so swipe handlers always read the latest value.
  const activeIndexRef = useRef(activeIndex);
  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  // Lock answer height to the tallest measured Q/A
  const answerWrapperRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [lockedAnswerHeight, setLockedAnswerHeight] = useState<number | null>(null);
  const [answerWidth, setAnswerWidth] = useState<number | null>(null);

  // Keep track of current animation + latest requested index
  const timelineRef = useRef<gsap.core.Timeline | null>(null);

  // Helper to compute scroll delta to center a target within the horizontal container.
  const computeMobileScrollDeltaToCenter = (
    container: HTMLDivElement,
    target: HTMLDivElement
  ) => {
    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();

    return (
      targetRect.left -
      containerRect.left -
      containerRect.width / 2 +
      targetRect.width / 2
    );
  };

  const bindSwipe = useDrag(
    ({ last, movement: [mx], velocity: [vx], direction: [dx] }) => {
      if (!last) return;

      const SWIPE_DISTANCE = 80;
      const SWIPE_VELOCITY = 0.1;

      const isSwipe =
        Math.abs(mx) > SWIPE_DISTANCE || Math.abs(vx) > SWIPE_VELOCITY;

      if (!isSwipe) return;

      const current = activeIndexRef.current;

      const nextIndex =
        dx < 0
          ? Math.min(current + 1, SHOWCASES.length - 1)
          : Math.max(current - 1, 0);

      if (nextIndex !== current) changeCategory(nextIndex);
    },
    {
      axis: 'x',
      filterTaps: true,
      pointer: { touch: true },
      preventScroll: true
    }
  );

  // Drag to scroll for the mobile stepper container
  const bindMobileStepperPan = useDrag(
    ({
      first,
      movement: [mx],
      memo,
      event,
      cancel,
      tap,
      down,
      dragging
    }) => {
      const container = mobileCategoriesContainerRef.current;
      if (!container) return memo;

      // If this was just a tap/click, don't interfere — let Step onClick fire.
      if (tap) return memo;

      // Only start dragging after a small horizontal movement threshold.
      // Before threshold is crossed, cancel so clicks remain unaffected.
      const DRAG_THRESHOLD = 8;
      if (down && !dragging && Math.abs(mx) < DRAG_THRESHOLD) {
        cancel();
        return memo;
      }

      // Once we're dragging, prevent native scrolling/gestures.
      if (event?.cancelable) event.preventDefault();

      if (first) {
        // Stop any auto-centering tween so user can take over immediately.
        gsap.killTweensOf(container);
        return container.scrollLeft;
      }

      // Natural drag: finger left -> scroll right (increase scrollLeft)
      container.scrollLeft = (memo as number) - mx;

      return memo;
    },
    {
      axis: 'x',
      filterTaps: true,
      pointer: { touch: true },
      eventOptions: { passive: false }
    }
  );

  useImperativeHandle(ref, () => ({
    container: mainContainerRef.current,
    content: contentRef.current,
    title: titleRef.current,
    paragraph: paragraphRef.current,
    questionBubble: questionRef.current,
    answerBubble: answerTextRef.current
  }));

  // Observe the real answer wrapper width so our hidden measurer wraps text identically
  useLayoutEffect(() => {
    const el = answerWrapperRef.current;
    if (!el) return;

    const compute = () => {
      // clientWidth matches how the content actually wraps
      const w = el.clientWidth;
      if (w > 0) setAnswerWidth(w);
    };

    compute();

    const ro = new ResizeObserver(compute);
    ro.observe(el);

    window.addEventListener('resize', compute);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', compute);
    };
  }, []);

  // Measure tallest Q/A with the same width as the visible layout and lock height
  useLayoutEffect(() => {
    const measurer = measureRef.current;
    if (!measurer) return;

    const measure = () => {
      if (!measurer) return;

      let max = 0;

      Array.from(measurer.children).forEach((node) => {
        const el = node as HTMLElement;

        // Force natural height
        el.style.height = 'auto';

        // scrollHeight captures pseudo-elements / tails
        max = Math.max(max, el.scrollHeight);
      });

      setLockedAnswerHeight(Math.ceil(max));
    };

    // Measure after layout settles (width/fonts)
    const raf = requestAnimationFrame(measure);

    const ro = new ResizeObserver(measure);
    ro.observe(measurer);

    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [answerWidth]);

  // Hide scrollbars for the mobile stepper container (but keep it scrollable programmatically)
  useLayoutEffect(() => {
    const el = mobileCategoriesContainerRef.current;
    if (!el) return;

    const prevOverflowX = el.style.overflowX;
    const prevOverflowY = el.style.overflowY;
    const prevScrollbarWidth = (el.style as any).scrollbarWidth;
    const prevMsOverflowStyle = (el.style as any).msOverflowStyle;
    const prevWebkitOverflowScrolling = (el.style as any).WebkitOverflowScrolling;

    // Keep scrollable so scrollLeft changes work but visually hide the bar
    el.style.overflowX = 'scroll';
    el.style.overflowY = 'hidden';
    (el.style as any).scrollbarWidth = 'none'; // Firefox
    (el.style as any).msOverflowStyle = 'none'; // IE/Edge legacy
    (el.style as any).WebkitOverflowScrolling = 'touch';

    const styleEl = document.createElement('style');
    styleEl.setAttribute('data-hide-stepper-scrollbar', 'true');
    styleEl.textContent = `
      .stepper-horizontal::-webkit-scrollbar { display: none; width: 0; height: 0; }
    `;
    document.head.appendChild(styleEl);

    return () => {
      el.style.overflowX = prevOverflowX;
      el.style.overflowY = prevOverflowY;
      (el.style as any).scrollbarWidth = prevScrollbarWidth;
      (el.style as any).msOverflowStyle = prevMsOverflowStyle;
      (el.style as any).WebkitOverflowScrolling = prevWebkitOverflowScrolling;

      if (styleEl.parentNode) styleEl.parentNode.removeChild(styleEl);
    };
  }, []);

  // Keep the active label visible on mobile (no scrollbar shown)
  const ensureMobileStepVisible = (index: number) => {
    const container = mobileCategoriesContainerRef.current;
    const target = mobileCategoryRefs.current[index];
    if (!container || !target) return;

    const cRect = container.getBoundingClientRect();
    const tRect = target.getBoundingClientRect();

    const PAD = 16;

    const fullyVisible =
      tRect.left >= cRect.left + PAD && tRect.right <= cRect.right - PAD;

    if (fullyVisible) return;

    const delta = computeMobileScrollDeltaToCenter(container, target);

    gsap.to(container, {
      scrollTo: { x: container.scrollLeft + delta },
      duration: 0.25,
      ease: 'power1.inOut',
      overwrite: 'auto'
    });
  };

// Do not ignore clicks during animation; interrupt & restart toward the latest intent
  const changeCategory = (index: number) => {
    // If it's already active, nothing to do
    if (index === activeIndexRef.current) return;

    // If a timeline is running, kill it so we can immediately transition to the new target
    if (timelineRef.current) {
      timelineRef.current.kill();
      timelineRef.current = null;
    }

    const container = mobileCategoriesContainerRef.current;
    if (container) gsap.killTweensOf(container);

    const target = mobileCategoryRefs.current[index];

    const tl = gsap.timeline({
      defaults: { ease: 'power1.inOut' },
      onComplete: () => {
        timelineRef.current = null;
        // ensureMobileStepVisible(activeIndexRef.current);
      }
    });

    timelineRef.current = tl;

    // Animate question OUT
    tl.to(questionRef.current, {
      opacity: 0,
      x: 32,
      duration: 0.25,
      ease: 'power1.in',
      overwrite: 'auto'
    });

    // Animate answer OUT
    tl.to(
      answerTextRef.current,
      {
        opacity: 0,
        x: -32,
        duration: 0.25,
        delay: 0.25,
        ease: 'power1.in',
        overwrite: 'auto'
      },
      '<'
    );

    // Mobile scroll in sync with OUT
    if (container && target) {
      const delta = computeMobileScrollDeltaToCenter(container, target);

      tl.to(
        container,
        {
          scrollTo: { x: container.scrollLeft + delta },
          duration: 0.25,
          ease: 'power1.inOut',
          overwrite: 'auto'
        },
        '<'
      );
    }

    // Swap content at the end of OUT
    tl.add(() => {
      setActiveIndex(index);
      activeIndexRef.current = index;
    });

    // Animate question IN
    tl.fromTo(
      questionRef.current,
      { opacity: 0, x: 32 },
      {
        opacity: 1,
        x: 0,
        duration: 0.25,
        delay: 0.25,
        ease: 'power1.out',
        immediateRender: false,
        overwrite: 'auto'
      }
    );

    // Animate answer IN
    tl.fromTo(
      answerTextRef.current,
      { opacity: 0, x: -32 },
      {
        opacity: 1,
        x: 0,
        duration: 0.25,
        delay: 0.25,
        ease: 'power1.out',
        immediateRender: false,
        overwrite: 'auto'
      },
      '<'
    );
  };

  const getTextColor = (variant: 'primary' | 'secondary' | 'tertiary') => {
    const colors = {
      primary: isDarkMode ? 'text-theme-primary-dark' : 'text-blue-700',
      secondary: isDarkMode ? 'text-theme-secondary-dark' : 'text-blue-700',
      tertiary: isDarkMode ? 'text-gray-500' : 'text-blue-500'
    };
    return colors[variant];
  };

  const getActiveTextColor = (isActive: boolean) =>
    isActive
      ? isDarkMode
        ? 'text-theme-secondary-dark'
        : 'text-blue-700'
      : isDarkMode
        ? 'text-gray-300'
        : 'text-blue-500';

  const QA_STEPPER_THEME: Partial<StepperTheme> = {
    ringGapColor: isDarkMode ? 'var(--background-colour-dark-mode)' : 'var(--background-colour-light-mode)',
    ringColor: isDarkMode ? 'var(--color-white)' : 'var(--color-blue-700)',
    lineInactiveColorClassName: isDarkMode ? 'bg-gray-600' : 'bg-blue-100',
    lineActiveColorClassName: isDarkMode ? 'bg-gray-300' : 'bg-blue-700',
    dotBaseColorClassName: isDarkMode ? 'bg-gray-600' : 'bg-blue-200',
    activeDotColorClassName: isDarkMode ? 'bg-gray-100' : 'bg-blue-700',
    completedDotColorClassName: isDarkMode ? 'bg-gray-100' : 'bg-blue-700'
  };

  const QA_STEPPER_UI = {
    dotSizeClassName: STEPPER_THEME.dotSizeClassName,
    label: {
      base: (active: boolean) =>
        `text-xl transition-colors ${getActiveTextColor(active)} opacity-60 group-hover:opacity-100`,
      active: `${getActiveTextColor(true)} opacity-100 font-semibold`,
      completed: `${getActiveTextColor(false)} opacity-60`
    },
    labelMobile: {
      base: (active: boolean) =>
        `whitespace-nowrap transition-colors ${getActiveTextColor(active)} opacity-60 group-hover:opacity-100`,
      active: `${getActiveTextColor(true)} opacity-100 font-semibold`,
      completed: `${getActiveTextColor(false)} opacity-60`
    }
  };

  const NavigationButtons = () => {
    const isPrevPossible = activeIndex > 0;
    const isNextPossible = activeIndex < SHOWCASES.length - 1;

    const getArrowColor = (isPossible: boolean) => {
      if (isPossible) return getTextColor('primary');
      return isDarkMode ? 'text-gray-600' : 'text-blue-300';
    };

    return (
      <div className="flex gap-6">
        <button
          onClick={() => isPrevPossible && changeCategory(activeIndex - 1)}
          className={`text-2xl font-semibold pl-1 lg:pl-0 p-2 ${
            isPrevPossible ? 'cursor-pointer' : 'cursor-default'
          } ${getArrowColor(isPrevPossible)}`}
          aria-label="Previous category"
          disabled={!isPrevPossible}
        >
          ‹
        </button>
        <button
          onClick={() => isNextPossible && changeCategory(activeIndex + 1)}
          className={`text-2xl font-semibold p-2 ${
            isNextPossible ? 'cursor-pointer' : 'cursor-default'
          } ${getArrowColor(isNextPossible)}`}
          aria-label="Next category"
          disabled={!isNextPossible}
        >
          ›
        </button>
      </div>
    );
  };

  const activeItem = SHOWCASES[activeIndex];

  // Keep active label visible if layout changes while on mobile
  useLayoutEffect(() => {
    ensureMobileStepVisible(activeIndex);
  }, [activeIndex]);

  return (
    <section
      ref={mainContainerRef}
      className="w-full min-h-screen overflow-hidden flex flex-col mx-auto pt-24 sm:pt-36 px-8 lg:px-16 xl:px-24"
    >
      <div ref={contentRef} className="w-full h-full flex flex-col">
        <div className="w-full mb-2 sm:mb-8 xl:mb-12">
          <h2
            ref={titleRef}
            className={`
              hidden
              lg:block
              main-title portrait-text-app text-left text-3xl 2xs:text-4xl xs:text-5xl lg:text-6xl font-bold mb-10
              ${getTextColor('primary')}
            `}
          >
            Ask about a word,<wbr /> the book or the author
          </h2>

          <h2
            className={`
              block
              lg:hidden
              main-title portrait-text-app text-left text-3xl 3xs:text-[1.95rem] 2xs:text-[2.165rem] xs:text-5xl sm:text-6xl font-bold mb-12
              ${getTextColor('primary')}
            `}
          >
            Ask about a word,<br /> the book or the author
          </h2>

          <p
            ref={paragraphRef}
            className={`
              main-paragraph text-lg xs:text-xl md:text-2xl lg:text-2xl max-w-4xl mb-10
              ${getTextColor('secondary')}
            `}
          >
            Aleph is capable of answering both simple and complex questions.
          </p>
        </div>

        {/* Hidden measurement layer (forced to same width as visible answer area) */}
        <div
          ref={measureRef}
          className="absolute -left-[9999px] top-0 pointer-events-none opacity-0"
          aria-hidden="true"
          style={answerWidth ? { width: `${answerWidth}px` } : undefined}
        >
          {SHOWCASES.map((item) => (
            <div key={item.id} className="max-w-3xl min-h-[200px]">
              <p
                className={`ml-[12.5%] text-md sm:text-lg lg:text-xl xl:text-2xl lg:mt-4 speech-bubble tail-right ${
                  isDarkMode ? 'text-white' : 'text-blue-700'
                }`}
              >
                {item.question}
              </p>

              <p
                className={`mr-[12.5%] mt-6 text-md sm:text-lg lg:text-xl xl:text-2xl speech-bubble tail-left text-gray-800`}
              >
                {item.answer}
              </p>
            </div>
          ))}
        </div>

        <div className={`p-4 2xs:p-8 lg:p-12 lg:pb-10 rounded-2xl `}>
          <div className="flex flex-col lg:flex-row gap-14 w-full min-h-0">
            <div className="hidden lg:flex flex-col w-1/3 min-h-0">
              <div
                className="relative flex flex-col my-1 pb-2"
                style={
                  lockedAnswerHeight
                    ? { height: `${lockedAnswerHeight}px` }
                    : undefined
                }
              >
                <div className="w-full h-full">
                  {/*  Desktop category stepper */}
                  <Stepper
                    activeStep={activeIndex}
                    orientation="vertical"
                    verticalMode="fill"
                    theme={QA_STEPPER_THEME}
                  >
                    {SHOWCASES.map((item, index) => (
                      <Step
                        key={item.id}
                        className={`${QA_STEPPER_UI.dotSizeClassName} group cursor-pointer`}
                        label={item.category}
                        labelClassName={`${QA_STEPPER_UI.label.base(false)}`}
                        activeLabelClassName={`${QA_STEPPER_UI.label.active}`}
                        completedLabelClassName={`${QA_STEPPER_UI.label.completed}`}
                        onClick={() => changeCategory(index)}
                      />
                    ))}
                  </Stepper>
                </div>
              </div>

              <div className="mt-6 stepper-nav-buttons">
                <NavigationButtons />
              </div>
            </div>

            <div className={`flex-1`}>
              <div
                ref={answerWrapperRef}
                className="max-w-2xl lg:max-w-3xl overflow-visible"
                style={
                  lockedAnswerHeight
                    ? { height: `${lockedAnswerHeight}px` }
                    : undefined
                }
              >
                <div {...bindSwipe()} className="min-h-[200px]">
                  <p
                    ref={questionRef}
                    className="ml-[12.5%] text-md sm:text-lg lg:text-xl xl:text-2xl speech-bubble tail-right text-white"
                    style={{ '--bubble-bg': '#0c0eff' } as React.CSSProperties}
                  >
                    {activeItem.question}
                  </p>

                  <p
                    ref={answerTextRef}
                    className={`mr-[12.5%] mt-6 text-md sm:text-lg lg:text-xl xl:text-2xl speech-bubble tail-left text-gray-800`}
                  >
                    {activeItem.answer}
                  </p>
                </div>
              </div>

              <div className="mt-4 xs:mt-14 lg:hidden">
                {/*  Mobile category stepper */}
                <div
                  ref={mobileCategoriesContainerRef}
                  className="w-full py-4 px-2 overflow-x-scroll overflow-y-hidden stepper-horizontal"
                  {...bindMobileStepperPan()}
                  style={{ touchAction: 'pan-y' } as React.CSSProperties}
                >
                  {/* Stepper becomes as wide as needed (intrinsic) */}
                  <Stepper
                    activeStep={activeIndex}
                    horizontalMode="intrinsic"
                    theme={QA_STEPPER_THEME}
                  >
                    {SHOWCASES.map((item, index) => (
                      <Step
                        key={item.id}
                        className={`${QA_STEPPER_UI.dotSizeClassName} group cursor-pointer`}
                        label={item.category}
                        labelClassName={`${QA_STEPPER_UI.labelMobile.base(false)}`}
                        activeLabelClassName={`${QA_STEPPER_UI.labelMobile.active}`}
                        completedLabelClassName={`${QA_STEPPER_UI.labelMobile.completed}`}
                        onClick={() => changeCategory(index)}
                        ref={(node) => {
                          mobileCategoryRefs.current[index] = node;
                        }}
                      />
                    ))}
                  </Stepper>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

QuestionsAnswers.displayName = 'QuestionsAnswers';
export default QuestionsAnswers;
